import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import AddressDisplay from './AddressDisplay'
import { required } from './config'
import {
  CreateBusItemViewModel,
  CreateFerryItemViewModel,
  CreateOtherTransportItemViewModel,
  CreateTrainItemViewModel,
} from '../../Create/models'
import {
  UpdateBusItemViewModel,
  UpdateFerryItemViewModel,
  UpdateOtherTransportItemViewModel,
  UpdateTrainItemViewModel,
} from '../../Edit/models'
import useEnsureGeoPlace from '../Hooks/useEnsureGeoPlace'

import AddressAutocomplete, {
  AddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/AddressAutocomplete'
import { useComponentRestrictions } from 'tripPlanner/hooks/Google/useComponentRestrictions'
import {
  ADDRESS_RESPONSE_DETAILS,
  AutocompletePlaceParam,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

type CreateOrUpdateItemViewModel =
  | CreateBusItemViewModel
  | UpdateBusItemViewModel
  | CreateFerryItemViewModel
  | UpdateFerryItemViewModel
  | CreateOtherTransportItemViewModel
  | UpdateOtherTransportItemViewModel
  | CreateTrainItemViewModel
  | UpdateTrainItemViewModel

type Props = {
  autocompleteTypes: AutocompletePlaceParam
  tripId: string
}

const START_GEO_DEP_KEYS: string[] = ['startPlace.name']
const END_GEO_DEP_KEYS: string[] = ['endPlace.name']

const StartEndAddressFields = ({ autocompleteTypes, tripId }: Props) => {
  const methods = useFormContext<CreateOrUpdateItemViewModel>()
  const { control, setValue, watch } = methods

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)
  const componentRestrictions = useComponentRestrictions(tripId)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: AddressAutocompleteOption,
    ) => {
      setValue(`${placeName}.name`, selectedOption.label)
      setValue(`${placeName}.type`, 'GEO')

      // eslint-disable-next-line @typescript-eslint/naming-convention
      if (selectedOption.details) {
        const { geometry, formatted_address, place_id, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        setValue(`${placeName}.id`, place_id)
        setValue(`${placeName}.address`, formatted_address)

        if (geometry && geometry.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat())
          setValue(`${placeName}.point.lng`, geometry.location.lng())
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: AddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: AddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  return (
    <>
      <Controller
        name="startPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <AddressAutocomplete
            {...rest}
            ref={ref}
            required
            label="Start location"
            placeholder="Search for location"
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleStartAddressChange}
            error={error?.message}
            initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {startPlace?.address && (
        <AddressDisplay title="Address" value={startPlace.address} />
      )}

      <Controller
        name="endPlace.name"
        control={control}
        rules={{ required }}
        render={({ field: { ref, ...rest }, fieldState: { error } }) => (
          <AddressAutocomplete
            {...rest}
            ref={ref}
            required
            label="End location"
            placeholder="Search for location"
            autocompleteTypes={autocompleteTypes}
            details={ADDRESS_RESPONSE_DETAILS}
            onPlaceSelect={handleEndAddressChange}
            error={error?.message}
            initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
            autocompletionReqParam={{
              componentRestrictions,
            }}
          />
        )}
      />
      {endPlace?.address && (
        <AddressDisplay title="Address" value={endPlace.address} />
      )}
    </>
  )
}

export default StartEndAddressFields
